import attribution from 'lib/tracking/attribution/attribution'
import axios from 'axios'
import { getCookieConsent } from 'lib/tracking/storage'

export enum GoogleAdsEventType {
    ViewContent = 'ViewContent',
    AddToCart = 'AddToCart',
    InitiateCheckout = 'InitiateCheckout',
    Purchase = 'Purchase',
    Lead = 'Lead',
    CompleteRegistration = 'CompleteRegistration',
    Search = 'Search',
    Contact = 'Contact',
    Subscribe = 'Subscribe',
    Download = 'Download',
    AddPaymentInfo = 'AddPaymentInfo',
    AddToWishlist = 'AddToWishlist',
    BeginCheckout = 'BeginCheckout',
    GenerateLead = 'GenerateLead',
    SignUp = 'SignUp',
    SubmitApplication = 'SubmitApplication',
    PageView = 'PageView',
}

export type GoogleAdsConversion = {
    conversion_id: string
    conversion_label: string
    conversion_action: string
    order_id?: string
    conversion_value?: number
    currency_code?: string
    conversion_date_time?: string
    user_identifiers?: GoogleAdsUserIdentifier[]
    user_agent?: string
    gclid?: string
    gbraid?: string
    wbraid?: string
    custom_variables?: { [key: string]: string | number }
}

export type GoogleAdsUserIdentifier = {
    hashed_email?: string
    hashed_phone_number?: string
    hashed_first_name?: string
    hashed_last_name?: string
    hashed_street_address?: string
    city?: string
    state?: string
    postal_code?: string
    country_code?: string
    user_identifier_source?: 'FIRST_PARTY' | 'THIRD_PARTY'
}

export type GoogleAdsConversionAdjustment = {
    adjustment_type: 'ENHANCEMENT' | 'RESTATEMENT' | 'RETRACTION'
    conversion_id: string
    conversion_label: string
    conversion_action: string
    order_id?: string
    conversion_value?: number
    currency_code?: string
    conversion_date_time?: string
    user_identifiers?: GoogleAdsUserIdentifier[]
    user_agent?: string
    gclid?: string
    gbraid?: string
    wbraid?: string
    custom_variables?: { [key: string]: string | number }
}

export type GoogleAdsAPIRequest = {
    customer_id: string
    conversion_adjustments: GoogleAdsConversionAdjustment[]
    partial_failure: boolean
    test_event_code?: string
}

const googleAdsEventQueue: {
    type: GoogleAdsEventType
    gclid?: string
    conversionData?: Partial<GoogleAdsConversion>
}[] = []

export function trackGoogleAdsEvent(
    event: GoogleAdsEventType,
    gclid?: string,
    conversionData?: Partial<GoogleAdsConversion>,
    testCode?: string
) {
    const attributionData = attribution.getAttribution()
    const consentCookie = getCookieConsent()

    if (!consentCookie?.statistics || !consentCookie?.marketing) {
        googleAdsEventQueue.push({
            type: event,
            gclid: gclid ?? attributionData.googleClickId,
            conversionData,
        })
        return
    }

    axios
        .post(
            '/api/google-ads-events',
            {
                eventName: event,
                testCode,
                gclid: gclid ?? attributionData.googleClickId,
                conversionData: {
                    order_id: generateOrderId(),
                    conversion_date_time: new Date().toISOString(),
                    user_agent: navigator.userAgent,
                    ...conversionData,
                },
            },
            {
                headers: {
                    'Content-Type': 'application/json',
                },
            }
        )
        .catch((error) => {
            console.error(`Google Ads Server Side Event: ${event} (${error.status})`)
        })
}

export const emptyGoogleAdsEventQueue = () => {
    if (!googleAdsEventQueue?.length) return

    for (const event of googleAdsEventQueue) {
        trackGoogleAdsEvent(event.type, event.gclid, event.conversionData)
    }
    // https://stackoverflow.com/questions/1232040/how-do-i-empty-an-array-in-javascript
    googleAdsEventQueue.length = 0
}

function generateOrderId(): string {
    return `order_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

// Helper function to hash user data using SHA-256
export async function hashUserData(data: string): Promise<string> {
    const encoder = new TextEncoder()
    const dataBuffer = encoder.encode(data.toLowerCase().trim())
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

// Helper function to normalize and hash email for Google Ads
export async function normalizeAndHashEmail(email: string): Promise<string> {
    let normalizedEmail = email.toLowerCase().trim()

    // Remove dots from Gmail addresses before @
    const emailParts = normalizedEmail.split('@')
    if (emailParts.length > 1 && (emailParts[1] === 'gmail.com' || emailParts[1] === 'googlemail.com')) {
        emailParts[0] = emailParts[0].replace(/\./g, '')
        normalizedEmail = `${emailParts[0]}@${emailParts[1]}`
    }

    return hashUserData(normalizedEmail)
}

export async function createUserIdentifiers(userData: {
    email?: string
    phone?: string
    firstName?: string
    lastName?: string
    street?: string
    city?: string
    state?: string
    postalCode?: string
    countryCode?: string
}): Promise<GoogleAdsUserIdentifier[]> {
    const identifiers: GoogleAdsUserIdentifier[] = []

    if (userData.email) {
        identifiers.push({
            hashed_email: await normalizeAndHashEmail(userData.email),
            user_identifier_source: 'FIRST_PARTY',
        })
    }

    if (userData.phone) {
        identifiers.push({
            hashed_phone_number: await hashUserData(userData.phone),
            user_identifier_source: 'FIRST_PARTY',
        })
    }

    if (userData.firstName || userData.lastName || userData.street || userData.city) {
        const addressIdentifier: GoogleAdsUserIdentifier = {
            user_identifier_source: 'FIRST_PARTY',
        }

        if (userData.firstName) addressIdentifier.hashed_first_name = await hashUserData(userData.firstName)
        if (userData.lastName) addressIdentifier.hashed_last_name = await hashUserData(userData.lastName)
        if (userData.street) addressIdentifier.hashed_street_address = await hashUserData(userData.street)
        if (userData.city) addressIdentifier.city = userData.city
        if (userData.state) addressIdentifier.state = userData.state
        if (userData.postalCode) addressIdentifier.postal_code = userData.postalCode
        if (userData.countryCode) addressIdentifier.country_code = userData.countryCode

        identifiers.push(addressIdentifier)
    }

    return identifiers
}
